import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tappable } from '@vectord/components';
import { concat } from 'lodash';
import cogoToast from 'cogo-toast';

import { EDIT_SBAT_GROUP_PAGE_TRACKINGS } from '@classCreation/data/editSbatGroupPageTrackings';
import { useBulkAddSuperBatchesMutation } from '@classCreation/services/sbatGroupService';
import analytics from 'utils/analytics';
import Form from 'components/Form';
import SuperBatchSelect from 'components/SuperBatchSelect';
import FieldErrors from 'components/FieldErrors';
import useSbatGroupConfirmation from '@classCreation/hooks/useSbatGroupConfirmation';

export default function CreateSuperBatchAcademyTopicForm({
  sbatGroup,
  onSuccess,
}) {
  const [superBatches, setSuperBatches] = useState([]);
  const [bulkAddSuperBatches, { isSuccess, error, isLoading: isAttaching }] =
    useBulkAddSuperBatchesMutation();

  const excludedSuperBatchIds = useMemo(() => {
    const currentSuperBatchIds = sbatGroup.super_batch_academy_topics.map(
      (sbat) => sbat.super_batch.id
    );
    const newSuperBatchIds = superBatches.map((o) => o.value);

    return concat(newSuperBatchIds, currentSuperBatchIds);
  }, [superBatches, sbatGroup.super_batch_academy_topics]);

  const handleCreateBatch = useCallback(() => {
    bulkAddSuperBatches({
      sbatGroupIds: [sbatGroup.id],
      superBatchIds: superBatches.map((o) => o.value),
    });
  }, [superBatches, bulkAddSuperBatches, sbatGroup.id]);

  const handleSubmit = useSbatGroupConfirmation(handleCreateBatch, sbatGroup);

  const handleOnChange = useCallback((newBatch) => {
    setSuperBatches(newBatch);
  }, []);

  useEffect(() => {
    if (isSuccess) {
      cogoToast.success('Super batch/es are successfully added!');
      analytics.click({
        click_type: EDIT_SBAT_GROUP_PAGE_TRACKINGS.addSuperBatches,
        custom: {
          class_name: sbatGroup.academy_topic.title,
          class_id: sbatGroup.id,
          number_of_batches: superBatches.length,
          super_batch_ids: superBatches.map((o) => o.value),
          super_batch_names: superBatches.map((o) => o.label),
        },
      });
      onSuccess();
    }
  }, [
    isSuccess,
    onSuccess,
    sbatGroup.academy_topic.title,
    sbatGroup.id,
    superBatches,
  ]);

  useEffect(() => {
    if (error) {
      cogoToast.error(
        error?.data?.message ||
          'There was some problem in creating the super batch. Please try again later!'
      );
    }
  }, [error]);

  return (
    <Form
      onSubmit={handleSubmit}
      className="p-b-xl"
    >
      <SuperBatchSelect
        value={superBatches}
        excludedSuperBatchIds={excludedSuperBatchIds}
        onChange={handleOnChange}
        isMulti
      />
      <FieldErrors
        name="super_batch_id"
        errors={error?.data?.errors}
      />
      <Tappable
        className="btn btn-primary m-v-xxs"
        disabled={isAttaching}
        component="button"
        type="submit"
      >
        Add new batch
      </Tappable>
    </Form>
  );
}
